import React from 'react';
import { Button, Checkbox, DropdonwFlags, InputText } from '@/components';
import { toCurrency } from '@/helpers/masks/toCurrency';
import PaymentType from '@/model/PaymentType';
import { updateMask as updateMaskCpfCnpj } from '@/helpers/masks/cpfCnpj';
import { CustomTable, TableColumn } from '@/components/Table';
import dayjs from 'dayjs';
import EventMainTrackingReport from '@/model/EventMainTrackingReport';
import Page from '@/model/Page';
import Pagination from '@/components/Utils/Pagination';
import PaymentStatus from '@/model/PaymentStatus';
import { DataList } from '@/components/DataList';
import { DataColumn } from '@/features/reverse/types';
import { FormInputFilterTracking, OptionsPdv } from '@/features/reports/types';
import { FormData, FormErrors, OnChangeFormInput, useHorizontalDragScroll } from '@/hooks';
import Section from '@/model/Section';
import UserType from '@/model/UserType';
import { Dialog } from '@/components/Dialog';
import { toast } from 'react-toastify';
import { FaFilePdf, FaQrcode } from 'react-icons/fa';
import { Col, Row } from 'reactstrap';
import { Info, Copy } from 'react-feather';
import QRCode from 'react-qr-code';
import OrderTicket from '@/model/OrderTicket';
import Search from '../../../../../assets/images/svg/search.svg';

export interface DataRowContractor {
  id: string;
  name: string;
  document: string;
  telephone: string;
  companyType: number;
  actions: string;
}

export interface TrackingContenttProps {
  formData: FormData;
  formErrors: FormErrors;
  pdvs?: OptionsPdv[];
  selectPdvs?: OptionsPdv[];
  tracking: EventMainTrackingReport | undefined;
  pagination: Page<OrderTicket, OrderTicket>;
  sections: Section[];
  selectSection: Section[];
  isFilter: boolean;
  userType: UserType;
  generatePDF: () => void;
  // onPage: (page: number) => void;
  onChangeFormInput: OnChangeFormInput;
  onChangeFilters: (id: string) => void;
  onChangeSection: (id: string) => void;
  clearFilters: () => void;
  submitFilter: (page: number) => void;
  onFilter: () => void;
  onPage: (page: number) => void;
}

export const TrackingContent: React.FC<TrackingContenttProps> = ({
  tracking,
  pagination,
  formData,
  formErrors,
  pdvs,
  selectPdvs,
  sections,
  isFilter,
  selectSection,
  userType,
  onChangeFormInput,
  // generatePDF,
  // onPage,
  onChangeFilters,
  clearFilters,
  submitFilter,
  onChangeSection,
  onFilter,
  onPage,
}) => {
  const horizontalDragScroll = useHorizontalDragScroll();
  const [selectedReportRow, setSelectedReportRow] = React.useState<OrderTicket | undefined>();
  const [modalType, setModalType] = React.useState<'detail' | 'qrcode' | undefined>();

  const getTicketType = (row: OrderTicket): string => {
    if (!row.ticketId) return 'Ingresso';
    return row.isHalfPrice ? 'Meia' : 'Inteira';
  };

  const handleSharePdf = async (pdfUrl: string): Promise<void> => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Ingresso PDF',
          text: 'Segue link do seu ingresso:',
          url: pdfUrl,
        });
        toast.success('Compartilhado com sucesso!');
      } catch (error) {
        const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
          `Segue o link do seu ingresso: ${pdfUrl}`,
        )}`;
        window.open(waUrl, '_blank');
      }
    } else {
      const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        `Segue o link do seu ingresso: ${pdfUrl}`,
      )}`;
      window.open(waUrl, '_blank');
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const convertNumberToStatus = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.APPROVED:
        return (
          <div className="filterActive" style={{ backgroundColor: '#7AD81B10' }}>
            <div style={{ color: '#7AD81B' }}>Aprovado</div>
          </div>
        );
      case PaymentStatus.DISAPPROVED:
        return (
          <div className="filterActive" style={{ backgroundColor: '#E54F4910' }}>
            <div style={{ color: '#E54F49' }}>Recusado</div>
          </div>
        );
      case PaymentStatus.AWAITING_PAYMENT:
        return (
          <div className="filterActive" style={{ backgroundColor: '#F6D93E10' }}>
            <div style={{ color: '#F6D93E' }}>Aguardando Pagamento</div>
          </div>
        );
      case PaymentStatus.CANCELED:
        return (
          <div className="filterActive" style={{ backgroundColor: '#E54F4910' }}>
            <div style={{ color: '#3CAFC8' }}>Cancelado</div>
          </div>
        );
      case PaymentStatus.CANCELLATION_ANALYSIS:
        return (
          <div className="filterActive" style={{ backgroundColor: '#E54F4910' }}>
            <div style={{ color: '#3CAFC8' }}>Analisando solicitação de cancelamento</div>
          </div>
        );
      default:
        return (
          <div className="filterActive" style={{ backgroundColor: '#F6D93E10' }}>
            <div style={{ color: '#F6D93E' }}>Pendente</div>
          </div>
        );
    }
  };
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const convertPaymentTypeToString = (types: PaymentType[]) => {
    if (types) {
      if (types.length === 1) {
        switch (types[0]) {
          case PaymentType.CREDIT_CARD:
            return 'Crédito';
          case PaymentType.TWO_CREDIT_CARDS:
            return 'Dois cartões';
          case PaymentType.DEBIT_CARD:
            return 'Débito';
          case PaymentType.PIX:
            return 'Pix';
          case PaymentType.BANK_SLIP:
            return 'Boleto';
          case PaymentType.MONEY:
            return 'Dinheiro';
          case PaymentType.COURTESY:
            return 'Cortesia';
          default:
            return 'Outros';
        }
      } else {
        const dataColumnProfiles: DataColumn[] = [];
        types.forEach((data, index) => {
          dataColumnProfiles.push({
            id: `${index}`,
            name: convertPaymentTypeToString([data]) as string,
          });
        });
        return <DropdonwFlags pointerClass={true} dataColumn={dataColumnProfiles} />;
      }
    } else {
      return '';
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const mountTransaction = (transactions: string[]) => {
    if (transactions) {
      if (transactions.length === 1) {
        return transactions[0];
      }
      const dataColumnProfiles: DataColumn[] = [];
      transactions.forEach((data, index) => {
        dataColumnProfiles.push({
          id: `${index}`,
          name: data as string,
        });
      });
      return <DropdonwFlags pointerClass={true} dataColumn={dataColumnProfiles} />;
    }
    return '';
  };

  const columns: TableColumn<OrderTicket>[] = [
    {
      name: 'Cód',
      cell: row => {
        const ticketIdStr = row.id || row.orderItemId;
        return (
          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            <span style={{ fontSize: '12px', opacity: 0.8 }} title={ticketIdStr}>
              {ticketIdStr}
            </span>
            <button
              onClick={e => {
                e.stopPropagation();
                navigator.clipboard.writeText(ticketIdStr);
                toast.success('ID copiado!');
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              title="Copiar ID"
            >
              <Copy size={14} color="#3CAFC8" />
            </button>
          </div>
        );
      },
      width: '240px',
    },
    {
      name: 'Setor e ingresso',
      cell: row =>
        row.ticketId
          ? `${row.sectionName} - ${row.isHalfPrice ? 'Meia' : 'Inteira'}`
          : row.sectionName,
      minWidth: '220px',
    },
    {
      name: 'CPF',
      cell: row => (row.clientCPF ? updateMaskCpfCnpj(row.clientCPF) : '-----'),
      minWidth: '130px',
    },
    {
      name: 'Status',
      cell: row => convertNumberToStatus(row.orderPaymentStatus),
      minWidth: '150px',
    },
    {
      name: 'Ações',
      cell: row => (
        <React.Fragment>
          <span title="Ver dados">
            <Info
              className="mr-4 action-icon"
              style={{ fontSize: '18px', cursor: 'pointer', verticalAlign: 'middle' }}
              color="#3CAFC8"
              onClick={() => {
                setSelectedReportRow(row);
                setModalType('detail');
              }}
            />
          </span>
          {row.pdfUrl && (
            <FaFilePdf
              className="mr-4 svg-icon action-icon"
              style={{
                color: '#3CAFC8',
                fontSize: '18px',
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
              title="Compartilhar PDF"
              onClick={() => handleSharePdf(row.pdfUrl || '')}
            />
          )}
          {row.qrCode && (
            <FaQrcode
              className="mr-4 svg-icon action-icon"
              style={{
                color: '#3CAFC8',
                fontSize: '18px',
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
              title="Visualizar QR Code"
              onClick={() => {
                setSelectedReportRow(row);
                setModalType('qrcode');
              }}
            />
          )}
        </React.Fragment>
      ),
      width: '180px',
    },
  ];
  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: 16 }}
      >
        <h5 style={{ margin: 0 }}>Rastreamento</h5>

        <div className="d-flex" style={{ gap: 10 }}>
          {/* <Button theme="outlineDark" onClick={() => generatePDF()} title="Exportar relatório" /> */}
          <Button theme="outlineDark" onClick={onFilter} title="Filtros" />
          {isFilter && <Button theme="outlineDark" onClick={clearFilters} title="Limpar filtros" />}
        </div>
      </div>
      <h6>Sumarizado</h6>
      <div
        className="customTableScroll p-4"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
          gridColumnGap: '40px',
          overflow: 'auto',
          alignItems: 'end',
        }}
      >
        <DataList
          data={
            tracking
              ? [
                  {
                    title: 'Vendidos:',
                    content: tracking.amountSold ? `${tracking.amountSold}` : '-----',
                  },
                  {
                    title: 'Cortesias:',
                    content: tracking.amountCourtesy ? tracking.amountCourtesy : '-----',
                  },
                  {
                    title: 'Faturamento:',
                    content: tracking.billingValue
                      ? `${toCurrency(tracking.billingValue)}`
                      : '-----',
                  },
                  {
                    title: 'Descontos:',
                    content: tracking.discounts ? `${toCurrency(tracking.discounts)}` : '-----',
                  },
                  {
                    title: 'Faturamento com descontos:',
                    content: tracking.billingValue
                      ? `${toCurrency(tracking.billingValue)}`
                      : '-----',
                  },
                ]
              : []
          }
        />
      </div>
      <h6>Vendas por tipo de pagamento</h6>
      <div
        className="customTableScroll p-4"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
          gridColumnGap: '40px',
          overflow: 'auto',
          alignItems: 'end',
        }}
      >
        <DataList
          data={
            tracking
              ? [
                  {
                    title: 'Crédito:',
                    content: tracking.sumaryPaymenteMethod.creditCard
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.creditCard)}`
                      : '-----',
                  },
                  {
                    title: 'Debito:',
                    content: tracking.sumaryPaymenteMethod.debitCard
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.debitCard)}`
                      : '-----',
                  },
                  {
                    title: 'Pix:',
                    content: tracking.sumaryPaymenteMethod.pix
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.pix)}`
                      : '-----',
                  },
                  {
                    title: 'Dinheiro:',
                    content: tracking.sumaryPaymenteMethod.money
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.money)}`
                      : '-----',
                  },
                ]
              : []
          }
        />
      </div>
      {isFilter && (
        <>
          <div
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid #5f5f5fff',
              backgroundColor: '#ffffffff',
              color: '#fff',
              fontWeight: 500,
              fontSize: '14px',
              transition: 'all 0.2s ease',
            }}
          >
            <h6 style={{ color: '#5f5f5fff', marginTop: 6 }}>Filtros</h6>
            <hr />
            {userType !== UserType.PDV && (
              <>
                <div className="input-label mt-3 mb-3">PDVs</div>
                <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                  {(pdvs ?? []).map(opt => (
                    <>
                      <div style={{ marginRight: 12 }}>
                        <Checkbox
                          key={String(opt.pdvID)}
                          name={`pdv-${opt.pdvID}`}
                          label={`-${String(opt.pdvName)}`}
                          checked={selectPdvs ? selectPdvs.some(s => s.pdvID === opt.pdvID) : false}
                          onChange={() => onChangeFilters(opt.pdvID)}
                        />
                      </div>
                    </>
                  ))}
                </div>
              </>
            )}
            <div className="input-label mt-4 mb-3">Setores</div>
            <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
              {(sections ?? []).map(section => (
                <>
                  <div style={{ marginRight: 12 }}>
                    <Checkbox
                      key={String(section.id)}
                      name={`section-${section.name}`}
                      label={`-${String(section.name)}`}
                      checked={selectSection ? selectSection.some(s => s.id === section.id) : false}
                      onChange={() => onChangeSection(section.id)}
                    />
                  </div>
                </>
              ))}
            </div>
            <div
              className="filters-grid  mt-5"
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr auto',
                gap: '16px',
                alignItems: 'end',
                marginTop: '16px',
              }}
            >
              <InputText
                name="itemLabel"
                type="text"
                label="Mesa"
                value={formData[FormInputFilterTracking.itemLabel]}
                onChange={e => {
                  onChangeFormInput(FormInputFilterTracking.itemLabel)(e.target.value);
                }}
                error={formErrors.itemLabel && formErrors.itemLabel[0]}
              />

              <InputText
                name="orderDateStart"
                type="date"
                label="Data inicial"
                value={formData[FormInputFilterTracking.orderDateStart]}
                onChange={e => {
                  onChangeFormInput(FormInputFilterTracking.orderDateStart)(e.target.value);
                }}
                error={formErrors.orderDateStart && formErrors.orderDateStart[0]}
              />

              <InputText
                name="orderDateEnd"
                type="date"
                label="Data final"
                value={formData[FormInputFilterTracking.orderDateEnd]}
                onChange={e => {
                  onChangeFormInput(FormInputFilterTracking.orderDateEnd)(e.target.value);
                }}
                error={formErrors.orderDateEnd && formErrors.orderDateEnd[0]}
              />

              <button
                type="button"
                onClick={() => submitFilter(pagination.page)}
                style={{
                  cursor: 'pointer',
                  height: 50,
                  padding: '0 12px',
                  boxSizing: 'border-box',
                  border: '1px solid #ccc',
                  borderRadius: 5,
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'end',
                  marginBottom: 55,
                }}
                aria-label="Buscar"
                title="Buscar"
              >
                <img src={Search} alt="" style={{ width: 24, height: 24 }} />
              </button>
            </div>
            <hr />
          </div>
        </>
      )}
      <div className="mt-5">
        <div
          ref={horizontalDragScroll.scrollRef}
          className="customTableScroll"
          style={{
            overflowX: 'auto',
            overflowY: 'visible',
            cursor: 'grab',
          }}
          onMouseDown={horizontalDragScroll.onMouseDown}
          onMouseMove={horizontalDragScroll.onMouseMove}
          onMouseUp={horizontalDragScroll.onMouseUp}
          onMouseLeave={horizontalDragScroll.onMouseLeave}
          onTouchStart={horizontalDragScroll.onTouchStart}
          onTouchMove={horizontalDragScroll.onTouchMove}
          onTouchEnd={horizontalDragScroll.onTouchEnd}
        >
          <CustomTable
            progressPending={false}
            columns={columns}
            data={pagination.list || []}
            numberRowsPerPage={pagination.total || 0}
          />
        </div>
        <Pagination
          currentPage={pagination.page}
          totalCount={pagination.total}
          pageSize={pagination.pageSize}
          onPageChange={page => onPage(page)}
          total={pagination.total}
        />
      </div>

      <Dialog
        title={modalType === 'detail' ? 'Ver dados' : 'Visualizar QR Code'}
        visible={modalType !== undefined}
        onClose={() => setModalType(undefined)}
        position="center"
        isContentWithCard={true}
        actions={[
          {
            title: 'Fechar',
            onClick: () => setModalType(undefined),
            theme: 'noneBorder',
          },
          {
            title: 'OK',
          },
        ]}
      >
        {modalType === 'detail' && selectedReportRow && (
          <div style={{ padding: '10px' }}>
            <Row className="mb-3">
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  Código do Ingresso (ID)
                </label>
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {selectedReportRow.id || selectedReportRow.orderItemId}
                </div>
              </Col>
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  Data e hora da utilização
                </label>
                <div style={{ fontSize: '14px' }}>
                  {dayjs(selectedReportRow.saleDate).format('DD/MM/YYYY [às] HH:mm')}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  Setor
                </label>
                <div style={{ fontSize: '14px' }}>{selectedReportRow.sectionName}</div>
              </Col>
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  Ingresso
                </label>
                <div style={{ fontSize: '14px' }}>{getTicketType(selectedReportRow)}</div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  CPF
                </label>
                <div style={{ fontSize: '14px' }}>
                  {selectedReportRow.clientCPF
                    ? updateMaskCpfCnpj(selectedReportRow.clientCPF)
                    : '-----'}
                </div>
              </Col>
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  PDV
                </label>
                <div style={{ fontSize: '14px' }}>{selectedReportRow.pdvName || '-----'}</div>
              </Col>
            </Row>

            <hr style={{ margin: '15px 0', borderColor: '#eee' }} />

            <Row className="mb-3">
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  Valor
                </label>
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {toCurrency(selectedReportRow.saleValue)}
                </div>
              </Col>
              <Col md={6}>
                <label className="text-muted" style={{ fontSize: '12px' }}>
                  Forma de Pagamento
                </label>
                <div style={{ fontSize: '14px' }}>
                  {selectedReportRow.payments
                    ? convertPaymentTypeToString(selectedReportRow.payments.map(p => p.paymentType))
                    : '-----'}
                </div>
              </Col>
            </Row>

            {selectedReportRow.payments && selectedReportRow.payments.some(p => p.transaction) && (
              <Row className="mb-3">
                <Col md={12}>
                  <label className="text-muted" style={{ fontSize: '12px' }}>
                    Código de Transação
                  </label>
                  <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                    {mountTransaction(
                      selectedReportRow.payments.filter(p => p.transaction).map(p => p.transaction),
                    )}
                  </div>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={12}>
                <label className="text-muted" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                  Links e Downloads
                </label>
                <div className="d-flex mt-2" style={{ gap: '10px' }}>
                  {selectedReportRow.pdfUrl ? (
                    <a
                      href={selectedReportRow.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm d-flex align-items-center"
                      style={{
                        gap: '5px',
                        borderColor: '#3CAFC8',
                        color: '#fff',
                        backgroundColor: '#3CAFC8',
                      }}
                    >
                      <FaFilePdf /> Baixar PDF
                    </a>
                  ) : (
                    <span className="text-muted" style={{ fontSize: '12px', alignSelf: 'center' }}>
                      PDF indisponível
                    </span>
                  )}

                  {selectedReportRow.qrCode ? (
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center"
                      style={{
                        gap: '5px',
                        borderColor: '#3CAFC8',
                        color: '#fff',
                        backgroundColor: '#3CAFC8',
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(selectedReportRow.qrCode || '');
                        toast.success('Token do QR Code copiado!');
                      }}
                    >
                      <FaQrcode /> Copiar Token QR Code
                    </button>
                  ) : (
                    <span
                      className="text-muted ml-3"
                      style={{ fontSize: '12px', alignSelf: 'center' }}
                    >
                      QR Code indisponível
                    </span>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        )}

        {modalType === 'qrcode' && selectedReportRow && (
          <div className="d-flex flex-column align-items-center justify-content-center p-4">
            {selectedReportRow.qrCode ? (
              <>
                <div
                  style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                  }}
                >
                  <QRCode value={selectedReportRow.qrCode} size={200} />
                </div>
                <div
                  className="mt-3 text-center text-muted"
                  style={{
                    fontSize: '12px',
                    maxWidth: '280px',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                  }}
                >
                  {selectedReportRow.qrCode}
                </div>
                <button
                  className="btn btn-sm mt-3"
                  style={{ backgroundColor: '#3CAFC8', borderColor: '#3CAFC8', color: '#fff' }}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedReportRow.qrCode || '');
                    toast.success('Token do QR Code copiado!');
                  }}
                >
                  Copiar Token
                </button>
              </>
            ) : (
              <div>QR Code indisponível</div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
};
